import JsonLd from "@/components/JsonLd.tsx";
import JsonLdBreadcrumbList from "@/components/JsonLdBreadcrumbList.tsx";
import {config} from "@/config/config";
import FaqComponent from "@/landing/sections/FaqComponent.tsx";
import {createFileRoute, Link} from '@tanstack/react-router'
import ctaBg from '../landing/assets/about_us_4.webp';
import iconApple from '../landing/assets/Apple.png';
import iconGoogle from '../landing/assets/Playstore.png';
import badmintonTournamentFeaturesBg from '../landing/assets/badminton-tournament-software/Badminton2.webp';
import badmintonTournamentWhyBg from '../landing/assets/badminton-tournament-software/Badminton5.webp';
import badmintonTournamentHeroBg from '../landing/assets/badminton-tournament-software/Badmintonhero.png';
import badmintonTournamentIcon3 from '../landing/assets/svgs/dynamic_icon.svg';
import badmintonTournamentIcon4 from '../landing/assets/svgs/player_team_icon.svg';
import badmintonTournamentIcon1 from '../landing/assets/svgs/scheduling_icon.svg';
import badmintonTournamentIcon2 from '../landing/assets/svgs/scoring_icon.svg';
import LandingNav from "../landing/components/LandingNav.tsx";
import ArrowUpRightIcon from "../landing/icons/ArrowUpRightIcon.tsx";
import CheckBadgeIcon from "../landing/icons/CheckBadgeIcon.tsx";
import FooterSection from "../landing/sections/FooterSection.tsx";

const FAQS = [
    {
        q: "How do I use ScorePal for my badminton tournament?",
        a: <>Simply set up your event, enter your players and teams, choose the bracket format, and ScorePal will manage the schedule, matches, and results automatically.</>,
    },
    {
        q: "Can I manage player registrations and memberships?",
        a: <>Yes! ScorePal handles the registration process and keeps player details and data, which allows organizers to track memberships easily.</>,
    },
    {
        q: "Does ScorePal support different tournament formats?",
        a: <>Absolutely. You can create single elimination, double elimination, round-robin, or custom badminton brackets for any event, acting as a reliable tournament league software solution.</>,
    },
    {
        q: "Can I track match results and player performance?",
        a: <>Yes, you can track scoring, rankings, and performance in real-time, giving both organizers and players full visibility of the tournament.</>,
    },
    {
        q: "Is ScorePal easy to use for clubs or schools?",
        a: <>Yes. ScorePal’s interface is designed for badminton events, making it easy to run tournaments, manage schedules, and handle participants without any technical skills.</>,
    },
    {
        q: "Can I share schedules and results with players?",
        a: <>Yes. Every schedule, match, and bracket can be shared online so players and participants can follow games, rounds, and final results in real time. </>,
    },
];

export const Route = createFileRoute('/badminton-tournament-software')({
    component: RouteComponent,
})

function RouteComponent() {
    const features = [
        {
            icon:        badmintonTournamentIcon1,
            title:       'Smart Match Scheduling',
            description: 'Generate accurate, conflict-free schedules in minutes. ScorePal accounts for court availability, match length, and player participation to keep your event running smoothly all day long.',
        },
        {
            icon:        badmintonTournamentIcon2,
            title:       'Real-Time Scoring & Live Results',
            description: 'Update scores instantly with ScorePal’s intuitive scoring tools. Brackets, leaderboards, and standings refresh automatically, ensuring players and spectators always have access to the latest results.',
        },
        {
            icon:        badmintonTournamentIcon3,
            title:       'Professional Bracket Management',
            description: 'Create clean, easy-to-follow brackets for single-elimination, double-elimination, round robin, or custom formats. ScorePal, tournament software badminton, keeps your tournament structure organized from first serve to final point.',
        },
        {
            icon:        badmintonTournamentIcon4,
            title:       'Streamlined Player Management ',
            description: 'Manage registrations, seedings, divisions, and player details all in one centralized system. ScorePal makes it simple to stay organized and reduce administrative work. It acts as a dependable badminton tournament manager for events of all sizes.',
        },
    ];

    const whyChooseItems = [
        'Automated scheduling, real-time live scoring, and simple sports analytics',
        'Flexible bracket and format options, including a smooth badminton bracket setup',
        'Perfect for clubs, academies, and school tournaments',
        'Mobile-friendly features for staff, volunteers, and officials',
        'Designed to save time and improve event accuracy',
    ];

    return (
        <div className="min-h-screen bg-[#EDEEF4]">
            <JsonLd id={`page${Route.id}/faq-schema`} item={{
                "@context":   "https://schema.org",
                "@type":      "FAQPage",
                "mainEntity": [
                    {
                        "@type":          "Question",
                        "name":           "How do I use ScorePal for my badminton tournament?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "Simply set up your event, enter your players and teams, choose the bracket format, and ScorePal will manage the schedule, matches, and results automatically."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "Can I manage player registrations and memberships?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "Yes! ScorePal handles the registration process and keeps player details and data, which allows organizers to track memberships easily."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "Does ScorePal support different tournament formats?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "Absolutely. You can create single elimination, double elimination, round-robin, or custom badminton brackets for any event, acting as a reliable tournament league software solution."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "Can I track match results and player performance?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "Yes, you can track scoring, rankings, and performance in real-time, giving both organizers and players full visibility of the tournament."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "Is ScorePal easy to use for clubs or schools?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "Yes. ScorePal’s interface is designed for badminton events, making it easy to run tournaments, manage schedules, and handle participants without any technical skills."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "Can I share schedules and results with players?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "Yes. Every schedule, match, and bracket can be shared online so players and participants can follow games, rounds, and final results in real time."
                        }
                    }
                ]
            }} />
            <JsonLd id={`page${Route.id}/app`} item={{
                "@context":            "https://schema.org",
                "@type":               "SoftwareApplication",
                "name":                "Badminton Tournament Software",
                "url":                 "https://scorepal.co/badminton-tournament-software/",
                "applicationCategory": "SportsApplication",
                "operatingSystem":     ["Web", "Android", "iOS"],
                "description":         "Streamline badminton tournaments with efficient bracket creation, match scheduling, and real-time scoring insights powered by ScorePal. Proven performance over the past 2 years.",
                "publisher":           {
                    "@type": "Organization",
                    "name":  "ScorePal",
                    "url":   "https://scorepal.co/"
                }
            }} />
            <JsonLdBreadcrumbList routeId={Route.id} crumbs={[{name: "Badminton Tournament Software", item: "https://scorepal.co/badminton-tournament-software/"}]} />
            <title>Badminton Tournament Software for Clubs & Organizers | Fast Scoring</title>
            <meta name="description" content="Badminton tournament software for clubs with league tools, live scoring, badminton league software & scoring system. Run events smoothly." />
            <link rel="canonical" href={`${config.publicUrl}/badminton-tournament-software/`} />

            <section className="relative overflow-hidden p-4">
                <div
                    className="flex flex-col min-h-screen justify-between relative pb-16 rounded-4xl bg-[#EDEEF4] bg-center bg-cover bg-no-repeat saturate-[0.87]"
                    style={{
                        backgroundImage: `url(${badmintonTournamentHeroBg})`
                    }}
                >
                    <div className="absolute inset-0 bg-linear-to-r from-black/60 via-black/30 to-transparent rounded-4xl" />

                    <div className="relative z-20">
                        <LandingNav />
                    </div>

                    <div className="container mx-auto px-6 lg:px-12 pt-8 flex-1 flex flex-col justify-center relative z-10">
                        <h1 className="font-bebas text-white uppercase text-[clamp(40px,6vw,72px)] leading-none font-bold tracking-wide mb-4">
                            Badminton Tournament <br />Software
                        </h1>

                        <p className="font-inter text-white/90 text-base lg:text-xl mb-8 max-w-2xl leading-relaxed">
                            Simplify Every Step of Your Badminton Tournament
                        </p>

                        <Link
                            to={"/signup/player/" as any}
                            className="flex items-center gap-2 bg-[#EDEEF4] text-[#082F8A] rounded-full pl-6 pr-1 py-1 w-fit font-semibold hover:bg-white transition"
                        >
                            <span className="font-inter text-base font-medium">Lets Get Started</span>
                            <div className="bg-[#082F8A] rounded-full p-1">
                                <ArrowUpRightIcon className="text-white" size={28} />
                            </div>
                        </Link>

                        <div className="flex flex-wrap gap-4 mt-16 lg:mt-60">
                            <a
                                href="https://play.google.com/store/apps/details?id=co.scorepal.mobile&pcampaignid=web_share"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block"
                            >
                                <div className="bg-white/10 rounded-xl px-6 py-3 flex items-center gap-3 hover:bg-gray-100 transition">
                                    <img src={iconGoogle} alt="Google Play" className="h-8" />
                                    <div className="text-left">
                                        <div className="text-white/70 text-xs">GET IT ON</div>
                                        <div className="text-white font-semibold text-sm">Google Play</div>
                                    </div>
                                </div>
                            </a>

                            <a
                                href="https://apps.apple.com/us/app/score-pal/id6745509167"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block"
                            >
                                <div className="bg-white/10 rounded-xl px-6 py-3 flex items-center gap-3 hover:bg-gray-900 transition">
                                    <img src={iconApple} alt="App Store" className="h-8" />
                                    <div className="text-left">
                                        <div className="text-white/70 text-xs">Download on the</div>
                                        <div className="text-white font-semibold text-sm">App Store</div>
                                    </div>
                                </div>
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-start">
                        <div>
                            <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-6">
                                Powerful, Easy-to-Use Tools<br />for Every Badminton Event
                            </h2>

                            <p className="font-inter text-[#1a1a2e]/80 text-base leading-relaxed">
                                Badminton tournament software enables smooth event handling with simple scoring, organized matches, and efficient tournament control for clubs..
                            </p>
                        </div>

                        <div className="hidden lg:block">
                            <img
                                src={badmintonTournamentFeaturesBg}
                                alt="Sports analytics"
                                className="w-full h-auto rounded-3xl object-cover"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-12">
                        {features.map((feature, index) => (
                            <div key={index} className="bg-white rounded-2xl p-6 shadow-sm">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 rounded-full bg-[#EDEEF4] flex items-center justify-center shrink-0">
                                        <img src={feature.icon} alt="" className="w-6 h-6" />
                                    </div>
                                    <h3 className="font-inter text-[#082F8A] font-semibold text-base">
                                        {feature.title}
                                    </h3>
                                </div>
                                <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">
                                    {feature.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <div className="flex justify-center">
                <Link
                    to={"/signup/player/" as any}
                    className="flex items-center gap-2 bg-[#EDEEF4] text-[#082F8A] rounded-full pl-6 pr-1 py-1 w-fit font-semibold hover:bg-white transition"
                >
                    <span className="font-inter text-base font-medium">Lets Get Started</span>
                    <div className="bg-[#082F8A] rounded-full p-1">
                        <ArrowUpRightIcon className="text-white" size={28} />
                    </div>
                </Link>
            </div>

            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
                        <div>
                            <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-8">
                                Why Badminton Organizers<br />Choose ScorePal’s Badminton Tournament Software
                            </h2>

                            <ul className="space-y-4">
                                {whyChooseItems.map((item, index) => (
                                    <li key={index} className="flex items-center gap-4">
                                        <CheckBadgeIcon size={24} />
                                        <span className="font-inter text-[#1a1a2e] text-base">
                                            {item}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="hidden lg:block">
                            <img
                                src={badmintonTournamentWhyBg}
                                alt="Why choose ScorePal Analytics"
                                className="w-full h-auto rounded-3xl object-cover"
                            />
                        </div>

                        <div className="block lg:hidden">
                            <img
                                src={badmintonTournamentWhyBg}
                                alt="Why choose ScorePal Analytics"
                                className="w-full h-auto rounded-3xl object-cover"
                            />
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-8 lg:py-16">
                <div className="container mx-auto px-6 lg:px-12">
                    <div
                        className="rounded-3xl py-16 lg:py-24 px-8 text-center bg-top bg-cover bg-no-repeat"
                        style={{backgroundImage: `url(${ctaBg})`}}
                    >
                        <h2 className="font-bebas text-white uppercase text-[clamp(28px,4vw,48px)] leading-tight font-bold tracking-wide mb-4">
                            Make Your Badminton Events Effortless
                        </h2>
                        <p className="font-inter text-white/90 text-base lg:text-lg mb-8 max-w-3xl mx-auto leading-relaxed">
                            ScorePal gives organizers everything needed to run smooth, professional badminton tournaments. Focus on creating a great competitive experience while ScorePal handles the logistics. Works smoothly with existing sports league software.
                            <br /><br />
                            Streamline your badminton tournaments with ScorePal badminton tournament software today.
                        </p>
                        <Link
                            to={"/signup/player/" as any}
                            className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition"
                        >
                            <span className="font-inter text-base font-medium">Lets Get Started</span>
                            <div className="bg-[#082F8A] rounded-full p-1">
                                <ArrowUpRightIcon className="text-white" size={28} />
                            </div>
                        </Link>
                    </div>
                </div>
            </section>

            <FaqComponent title={"FAQs About Badminton Tournament Software"} faqs={FAQS} />

            <FooterSection />
        </div>
    );
}
